import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { LIFECYCLE } from "@/lib/constants";

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    api.get("/admin/metrics").then((r) => setMetrics(r.data));
  }, []);

  if (!metrics) return <div className="text-muted-foreground">Loading metrics…</div>;

  const chartData = LIFECYCLE.map((s) => ({ status: s, count: metrics.by_status[s] || 0 }));

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total cases" value={metrics.total_cases} />
        <KPI label="Individuals" value={metrics.users.individual} />
        <KPI label="Businesses" value={metrics.users.business} />
        <KPI label="Mediators / Arbitrators" value={metrics.users.neutral} accent />
      </div>

      <div className="flex flex-wrap gap-3">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/export/csv`} target="_blank" rel="noreferrer">
          <button className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] border border-ink/25 text-ink hover:border-clay hover:text-clay transition-colors" style={{ borderRadius: 3 }} data-testid="export-csv">Export CSV ↓</button>
        </a>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/export/pdf`} target="_blank" rel="noreferrer">
          <button className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] border border-ink/25 text-ink hover:border-clay hover:text-clay transition-colors" style={{ borderRadius: 3 }} data-testid="export-pdf">Export PDF ↓</button>
        </a>
      </div>

      <div className="bg-card rounded-sm border border-stone/60 p-6 ">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-court">Cases by status</h3>
          <Link to="/app/cases" className="text-sm text-court hover:underline">Go to all cases →</Link>
        </div>
        <div className="h-80">
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ left: -10, right: 20, top: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="status" angle={-25} textAnchor="end" interval={0} height={70} stroke="#4A5568" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} stroke="#4A5568" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#1E3A5F" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <QuickLink to="/app/admin/neutrals" label="Empanelment queue" desc="Approve / activate mediators & arbitrators" />
        <QuickLink to="/app/admin/leads" label="Contact leads" desc={`${metrics.leads} from marketing site`} />
        <QuickLink to="/app/admin/users" label="User & org management" desc="View all platform users" />
      </div>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`rounded-sm p-6 border ${accent ? "bg-emerald-50 border-emerald-200" : "bg-card border-stone/60"} `} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-4xl font-semibold mt-2 text-court">{value}</div>
    </div>
  );
}

function QuickLink({ to, label, desc }) {
  return (
    <Link to={to} className="rounded-sm bg-card border border-stone/60 p-6 lift-on-hover  block">
      <div className="font-display text-lg text-court">{label}</div>
      <div className="text-sm text-muted-foreground mt-1">{desc}</div>
    </Link>
  );
}
